import {
  BackgroundType,
  EducationTalent,
  ITalent,
  ItemJobs,
  ItemLanguages,
  ItemSkills,
  Language,
} from '@bloomays-lib/types.shared';
import { TalentFormationsSection, TalentLanguagesSection, Modal } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import ItemsEdition from './ItemsEdition';
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { FIND_TALENT, DELETE_EDUCATION, UPSERT_EDUCATION, UPSERT_LANGUAGE } from '@bloomays-lib/adapter.api-talent';
import { notify } from '../../helpers/toastify';
import { useTheme } from '@mui/material/styles';
import EducationEdition from './EducationEdition';
import { capitalize, findDuplicate } from '@bloomays-lib/utils.shared';
import { errorLogger } from '../../helpers/error';

export type TalentSheetFormationProps = {
  talent?: ITalent;
};

const TalentSheetFormation = ({ talent }: TalentSheetFormationProps) => {
  const { t } = useTranslation(['talentSheet', 'random', 'languages', 'notify']);
  const theme = useTheme();
  const [educations, setEducations] = useState(talent?.educations);
  const [languages, setLanguages] = useState<ItemLanguages[]>();
  const [newLanguages, setNewLanguages] = useState<ItemLanguages[]>([]);
  const [education] = useState<EducationTalent>({
    id: '',
    type: BackgroundType.Education,
    name: '',
    label: '',
    talentId: talent?.id,
  });
  const [open, setOpen] = useState<{
    items: ItemJobs[] | ItemSkills[] | ItemLanguages[];
    show: boolean;
    type: 'skills' | 'languages';
  }>({ items: [], show: false, type: 'skills' });
  const [openEduc, setOpenEduc] = useState<{
    item: EducationTalent | undefined;
    show: boolean;
  }>({ item: undefined, show: false });

  const handleCloseEduc = () => {
    setOpenEduc({ ...openEduc, show: false });
  };

  const [upsertLanguages, { loading: loadingLang, error: errorLanguages }] = useMutation<{
    upsertLanguages: Language[];
  }>(UPSERT_LANGUAGE, {
    context: { clientName: 'api.talents' },
    refetchQueries: [
      {
        query: FIND_TALENT,
        variables: {
          id: talent?.id,
        },
        context: { clientName: 'api.talents' },
      },
    ],
  });

  const [upsertEducationTalent, { loading: loadingEducTalent }] = useMutation<{
    upsertEducationTalent: EducationTalent;
  }>(UPSERT_EDUCATION, {
    context: { clientName: 'api.talents' },
    refetchQueries: [
      {
        query: FIND_TALENT,
        variables: {
          id: talent?.id,
        },
        context: { clientName: 'api.talents' },
      },
    ],
  });

  const [deleteEducationTalent] = useMutation<{
    deleteEducationTalent: EducationTalent;
  }>(DELETE_EDUCATION, {
    context: { clientName: 'api.talents' },
    refetchQueries: [
      {
        query: FIND_TALENT,
        variables: {
          id: talent?.id,
        },
        context: { clientName: 'api.talents' },
      },
    ],
  });

  const handleClose = () => {
    setOpen({ ...open, show: false });
    setLanguages(languages?.filter((lang) => lang.label !== ''));
  };

  useEffect(() => {
    if (talent) {
      setEducations(talent.educations);
      setLanguages(
        talent.languages?.map((lang) => {
          return { ...lang, selected: true, id: lang.id as string };
        }),
      );
    }
  }, [talent]);

  const listItemsLang = [
    capitalize(t('french', { ns: 'languages' })),
    capitalize(t('english', { ns: 'languages' })),
    capitalize(t('spanish', { ns: 'languages' })),
    capitalize(t('german', { ns: 'languages' })),
    capitalize(t('russian', { ns: 'languages' })),
    capitalize(t('chinese', { ns: 'languages' })),
    capitalize(t('korean', { ns: 'languages' })),
    capitalize(t('arabic', { ns: 'languages' })),
    capitalize(t('portuguese', { ns: 'languages' })),
    capitalize(t('italian', { ns: 'languages' })),
  ];

  const upsertLang = async () => {
    const preparedLang = newLanguages.map((lang) => {
      delete lang.selected;
      return { ...lang, talentId: talent?.id };
    });
    const double = findDuplicate(languages || []);
    if (double.length > 0) {
      notify('warning', t('duplicateLangs'), null, {
        hideProgressBar: true,
        autoClose: 10000,
      });
      return;
    }
    const updated = await upsertLanguages({
      variables: {
        languageTalent: preparedLang,
      },
    });
    if (updated.data?.upsertLanguages && updated.data?.upsertLanguages?.length > 0) {
      notify('success', t('modifSaved', { ns: 'random' }), null, {
        hideProgressBar: false,
        autoClose: 3000,
      });
      setNewLanguages([]);
      handleClose();
    }
  };

  if (errorLanguages) {
    errorLogger(errorLanguages, {
      extraInfos: 'Internal server error, impossible to retrieve jobs data !',
    });
    notify('error', t('randomLKError', { ns: 'notify' }), errorLanguages);
  }

  const upsertEduc = async (educ: EducationTalent) => {
    const updated = await upsertEducationTalent({
      variables: {
        education: { ...educ, talentId: talent?.id },
      },
    });
    if (updated.data?.upsertEducationTalent && updated.data?.upsertEducationTalent?.id) {
      notify('success', t('modifSaved', { ns: 'random' }), null, {
        hideProgressBar: false,
        autoClose: 3000,
      });
      handleCloseEduc();
    }
  };

  const deleteEducation = async (id?: string) => {
    const deleted = await deleteEducationTalent({
      variables: {
        educationTalentId: id,
      },
    });
    if (deleted.data?.deleteEducationTalent && deleted.data?.deleteEducationTalent?.id) {
      notify('success', t('modifSaved', { ns: 'random' }), null, {
        hideProgressBar: false,
        autoClose: 3000,
      });
      handleCloseEduc();
    }
  };

  return (
    <div>
      <Modal
        open={open.show}
        onClose={handleClose}
        additionnalCSS={{
          overflowY: 'scroll',
          border: 'none',
          backgroundColor: theme.palette.neutral[10],
          borderRadius: '20px',
        }}
      >
        <ItemsEdition
          talentId={talent?.id}
          loading={loadingLang}
          items={languages || []}
          type={open.type}
          setNewRow={setLanguages}
          setNews={setNewLanguages} // TODO
          upsert={upsertLang}
          itemList={listItemsLang}
        />
      </Modal>
      <Modal
        open={openEduc.show}
        onClose={handleCloseEduc}
        additionnalCSS={{
          border: 'none',
          borderRadius: '20px',
          backgroundColor: theme.palette.neutral[10],
        }}
      >
        <EducationEdition
          loading={loadingEducTalent}
          item={openEduc.item}
          upsert={upsertEduc}
          deleteEduc={deleteEducation}
        />
      </Modal>
      <TalentLanguagesSection
        languages={languages || []}
        addActionFn={() => {
          setOpen({
            type: 'languages',
            items: languages || [],
            show: true,
          });
        }}
      />
      <TalentFormationsSection
        educations={educations || []}
        setOpenEduc={setOpenEduc}
        addActionFn={() => {
          setOpenEduc({
            item: education,
            show: true,
          });
        }}
      />
    </div>
  );
};

export default TalentSheetFormation;
export { TalentSheetFormation };
